import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { websocketService } from '../services/websocket';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // Load notifications from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('notifications');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setNotifications(parsed);
        setUnreadCount(parsed.filter(n => !n.read).length);
      } catch (err) {
        console.error('Error loading notifications:', err);
      }
    }
  }, []);

  // Save notifications to localStorage whenever they change
  useEffect(() => {
    if (notifications.length > 0) {
      localStorage.setItem('notifications', JSON.stringify(notifications));
    }
  }, [notifications]);

  // Add a new notification
  const addNotification = useCallback((notification) => {
    const newNotification = {
      id: Date.now().toString(),
      ...notification,
      timestamp: notification.timestamp || new Date().toISOString(),
      read: false
    };

    setNotifications(prev => [newNotification, ...prev].slice(0, 50)); // Keep last 50 notifications
    setUnreadCount(prev => prev + 1);

    // Show browser notification if permitted
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(notification.title, {
        body: notification.message,
        icon: '/logo192.png',
        tag: newNotification.id
      });
    }
  }, []);

  // Mark notification as read
  const markAsRead = useCallback((notificationId) => {
    setNotifications(prev =>
      prev.map(n =>
        n.id === notificationId ? { ...n, read: true } : n
      )
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
  }, []);

  // Mark all as read
  const markAllAsRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  }, []);

  // Clear all notifications
  const clearAll = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0);
    localStorage.removeItem('notifications');
  }, []);

  // Delete a specific notification
  const deleteNotification = useCallback((notificationId) => {
    setNotifications(prev => {
      const updated = prev.filter(n => n.id !== notificationId);
      const wasUnread = prev.find(n => n.id === notificationId)?.read === false;
      if (wasUnread) {
        setUnreadCount(count => Math.max(0, count - 1));
      }
      return updated;
    });
  }, []);

  // Request notification permission
  const requestPermission = useCallback(async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      await Notification.requestPermission();
    }
  }, []);

  // Setup WebSocket listeners for real-time notifications
  useEffect(() => {
    const handleNotification = (data) => {
      console.log('Received notification:', data);
      addNotification(data);
    };

    try {
      websocketService.connect();
      websocketService.subscribe('notification', handleNotification);
      websocketService.subscribe('appointment:confirmed', (data) => {
        addNotification({
          type: 'appointment',
          title: 'Appointment Confirmed',
          message: data.message || `${data.doctorName} - ${data.specialization}`,
          icon: 'calendar-check',
          data: data
        });
      });
      websocketService.subscribe('lab:results', (data) => {
        addNotification({
          type: 'lab-results',
          title: 'Lab Results Available',
          message: data.message || `${data.testName} - Results ready`,
          icon: 'file-plus',
          data: data
        });
      });
      websocketService.subscribe('payment:success', (data) => {
        addNotification({
          type: 'payment',
          title: 'Payment Processed',
          message: data.message || `${data.description} - ৳${data.amount}`,
          icon: 'credit-card',
          data: data
        });
      });
    } catch (error) {
      console.error('Error setting up notification listeners:', error);
    }

    return () => {
      try {
        websocketService.unsubscribe('notification', handleNotification);
      } catch (error) {
        console.error('Error cleaning up notification listeners:', error);
      }
    };
  }, [addNotification]);

  const value = {
    notifications,
    unreadCount,
    addNotification,
    markAsRead,
    markAllAsRead,
    clearAll,
    deleteNotification,
    requestPermission
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;

